import React from 'react';
import styled from 'styled-components';
import { CheckCircle } from '@styled-icons/boxicons-regular';
import { Block } from '@styled-icons/boxicons-regular';
import { useStore } from '../../Context';
import Button from '../Button';
import useTheme from '../../hooks/useTheme';

const success = {
    header: '¡La firma electrónica se realizó correctamente!',
    text: 'Te van a contactar telefónicamente para continuar con el proceso.',
    button: 'Aceptar',
};

const retry = {
    header: '¡No pudimos validar tu firma electrónica!',
    text: 'Necesitamos que vuelvas a intentarlo. No pudimos procesar la información correctamente.',
    button: 'Reintentar',
};

const document = {
    header: '¡Importante!',
    text: 'Para comenzar necesitamos validar tu identidad. Por favor, tené a mano tu DNI.',
    button: 'Aceptar',
};

const retrySelfies = {
    header: '¡No pudimos acceder a la cámara!',
    text: 'Necesitamos que vuelvas a intentarlo.',
    button: 'Reintentar',
};

const retrySignature = {
    header: '¡La firma no se pudo completar!',
    text: 'Por favor, realice una firma más grande',
    button: 'Reintentar',
};

const faceValidationRetry = {
    type: 'faceValidationRetry',
    header: '¡No pudimos validar tu identidad!',
    text: 'No pudimos procesar la verificación facial correctamente. Por favor, intentá nuevamente.',
    button: 'Reintentar',
};

const onCancelFaceliveness = {
    type: 'onCancelFaceliveness',
    header: 'Verificación cancelada',
    text: 'Cancelaste la verificación facial. Podés volver a intentarlo.',
    button: 'Reintentar',
};

const facelivenessTimeout = {
    type: 'facelivenessTimeout',
    header: '¡Tiempo agotado!',
    text: 'El tiempo para completar la verificación facial expiró. Por favor, intentá nuevamente.',
    button: 'Reintentar',
};

const facelivenessCreateSessionError = {
    type: 'facelivenessCreateSessionError',
    header: '¡Error al iniciar la sesión!',
    text: 'No pudimos iniciar la sesión de verificación facial. Por favor, intentá nuevamente.',
    button: 'Reintentar',
};

export const modalTypes = {
    faceValidationRetry,
    onCancelFaceliveness,
    facelivenessTimeout,
    facelivenessCreateSessionError,
};

const Wrapper = styled.div`
    height: 100%;
    width: 80%;
`;

const DivIcon = styled.div`
    width: 100%;
    text-align: center;
    padding-bottom: 0.3rem;
`;

const SuccessIcon = styled(CheckCircle)`
    color: green;
    height: 2.5rem;
    width: 2.5rem;
`;

const RetryIcon = styled(Block)`
    height: 2.5rem;
    width: 2.5rem;
    color: red;
`;

const Header = styled.h4`
    font-size: 1rem;
    text-align: center;
    padding-bottom: 0.5rem;
`;

const Text = styled.p`
    font-size: 0.8rem;
    text-align: center;
    padding-bottom: 1rem;
`;

function SignatureModal({ modal, id }) {
    const { closeModal, stepForward, stepBackwards, photoUri } = useStore();
    const { type, message } = modal;

    const theme = useTheme()
    
    function handleCloseSelfieModal () {
        photoUri.neutralSelfie = null;
        photoUri.specialSelfie = null;
        stepBackwards();
        closeModal();
    };

    function handleCloseDocumentModal () {
        photoUri.front = null;
        photoUri.back = null;
        stepBackwards();
        closeModal();
    };

    if (type === 'success') {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <SuccessIcon />
                </DivIcon>
                <Header>{success.header}</Header>
                {theme === 'cuotitas' && <Text>{success.text}</Text>}
                <Button
                    filled
                    onClick={() => {
                        closeModal();
                        stepForward();
                    }}
                >
                    {success.button}
                </Button>
            </Wrapper>
        );
    } 
    if (type === 'document') {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <img className='mx-auto' src={`/${theme}/icon-alert.svg`} alt="" width="35" />
                </DivIcon>
                <Header>{document.header}</Header>
                <Text>{document.text}</Text>
                <Button
                    filled
                    onClick={() => {
                        closeModal();
                    }}
                >
                    {document.button}
                </Button>
            </Wrapper>
        );
    } 
    if (type === 'retry') {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{retry.header}</Header>
                <Text>{retry.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={() => closeModal()}>{retry.button}</Button>
            </Wrapper>
        );
    } 
    if (type === 'retryDocument') {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{retrySelfies.header}</Header>
                <Text>{retrySelfies.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={handleCloseDocumentModal}>{retrySelfies.button}</Button>
            </Wrapper>
        );
    } 
    if (type === 'retrySelfies') {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{retrySelfies.header}</Header>
                <Text>{retrySelfies.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={handleCloseSelfieModal}>{retrySelfies.button}</Button>
            </Wrapper>
        );
    }
    if (type === 'retrySignature') {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{retrySignature.header}</Header>
                <Text>{retrySignature.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={() => closeModal()}>{retrySignature.button}</Button>
            </Wrapper>
        );
    }
    if (type === faceValidationRetry.type) {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{faceValidationRetry.header}</Header>
                <Text>{faceValidationRetry.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={() => closeModal()}>{faceValidationRetry.button}</Button>
            </Wrapper>
        );
    }
    if (type === onCancelFaceliveness.type) {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <img className='mx-auto' src={`/${theme}/icon-alert.svg`} alt="" width="35" />
                </DivIcon>
                <Header>{onCancelFaceliveness.header}</Header>
                <Text>{onCancelFaceliveness.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={() => closeModal()}>{onCancelFaceliveness.button}</Button>
            </Wrapper>
        );
    }
    if (type === facelivenessTimeout.type) {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{facelivenessTimeout.header}</Header>
                <Text>{facelivenessTimeout.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={() => closeModal()}>{facelivenessTimeout.button}</Button>
            </Wrapper>
        );
    }
    if (type === facelivenessCreateSessionError.type) {
        return (
            <Wrapper id={id}>
                <DivIcon>
                    <RetryIcon />
                </DivIcon>
                <Header>{facelivenessCreateSessionError.header}</Header>
                <Text>{facelivenessCreateSessionError.text}</Text>
                {message && <Text>{message}</Text>}
                <Button filled onClick={() => closeModal()}>{facelivenessCreateSessionError.button}</Button>
            </Wrapper>
        );
    }
    return null;
}

export default SignatureModal;
